import React, {useCallback, useEffect} from "react";
import ChatterApp from "./ChatterApp";
import {WebWorkerProvider} from "./context/webWorkerProvider";
import {initGA} from "./index";
import {CurrentRaceEventProvider} from "./context/currentRaceEventProvider";
import {LoginProvider} from "./context/login.context";
import {StreamDetailsProvider} from "./context/StreamDetailsProvider";
import {StyledEngineProvider, ThemeProvider} from "@mui/material";
import {darkTheme} from "./styles/themes/darkTheme";
import {BoatCountryCodesProvider} from "./context/BoatCountryCodesProvider";
import {VideoReplaySyncProvider} from "./context/videoReplaySync.provider";
import {NavigationSliderProvider} from "./context/navigationSliderProvider";
import {useWindowSize} from "./hooks/useWindowSize";
import {MobileDownloadPrompt} from "./components/MobileDownloadPrompt";
import RouteChangeTracker from "./utils/routeChangeTracker";
import AutoPlayProvider from "./context/autoPlayProvider";
import {RaceDashboardLayoutProvider} from "./context/RaceDashboardLayoutProvider";
import {TeamDataProvider} from "./context/TeamDataProvider";
import CookieBanner from "./components/CookieBanner/CookieBanner";

const App = () => {

    // Optimized dataLayer initialization
    const initializeDataLayer = useCallback(() => {
        if (typeof window === 'undefined') return;

        window.dataLayer = window.dataLayer || [];

        const getCookie = (name: string): string | undefined => {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) return parts.pop()?.split(';').shift();
        };

        let savedConsent = getCookie('cookieConsent');
        if (!savedConsent) {
            try {
                savedConsent = localStorage.getItem('cookieConsent');
            } catch (e) {
                console.error('Failed to access localStorage:', e);
            }
        }
        
        if (savedConsent) {
            try {
                const consentSettings = JSON.parse(savedConsent);

                // Call official Google Consent Mode v2 API to restore consent
                if (window.gtag) {
                    window.gtag('consent', 'update', {
                        ad_storage: consentSettings.ad_storage,
                        analytics_storage: consentSettings.analytics_storage,
                        ad_user_data: consentSettings.ad_user_data,
                        ad_personalization: consentSettings.ad_personalization,
                        functionality_storage: consentSettings.functionality_storage || 'granted',
                        personalization_storage:
                            consentSettings.personalization_storage ||
                            consentSettings.ad_personalization ||
                            'denied',
                    });
                }

                // Keep existing custom event for backwards compatibility. This can be removed in the future once tags are fully migrated to Consent Mode v2.
                window.dataLayer.push({
                    event: 'cookieConsentUpdate',
                    cookie_banner_consent: consentSettings,
                });
            } catch (e) {
                console.error('Failed to parse consent settings:', e);
            }
        } else {
            window.dataLayer.push({
                event: 'cookieConsentInitial',
                cookie_banner_consent: {
                    ad_storage: 'denied',
                    analytics_storage: 'denied',
                    ad_user_data: 'denied',
                    ad_personalization: 'denied',
                },
            });
        }
    }, []);

    useEffect(() => {
        initGA();
        initializeDataLayer()
    }, []);

    const {width} = useWindowSize();

    // on mobile devices we should be pushing users to the App
    return width <= 1024 || !width ? (
        <MobileDownloadPrompt/>
    ) : (
        <LoginProvider>
            <BoatCountryCodesProvider>
                <WebWorkerProvider>
                    <CurrentRaceEventProvider>
                        <TeamDataProvider>
                            <NavigationSliderProvider>
                                <StreamDetailsProvider>
                                    <VideoReplaySyncProvider>
                                        <StyledEngineProvider injectFirst>
                                            <RaceDashboardLayoutProvider>
                                                <AutoPlayProvider>

                                                    <ThemeProvider theme={darkTheme}>
                                                        <RouteChangeTracker/>
                                                        <CookieBanner/>
                                                        <ChatterApp/>
                                                    </ThemeProvider>

                                                </AutoPlayProvider>
                                            </RaceDashboardLayoutProvider>
                                        </StyledEngineProvider>
                                    </VideoReplaySyncProvider>
                                </StreamDetailsProvider>
                            </NavigationSliderProvider>
                        </TeamDataProvider>
                    </CurrentRaceEventProvider>
                </WebWorkerProvider>
            </BoatCountryCodesProvider>
        </LoginProvider>
    );
};

export default App;
